import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import caretRightUrl from '../../assets/signals/caret-right.svg';
import closeUrl from '../../assets/signals/close.svg';
import unreadUrl from '../../assets/signals/unread.svg';
import { formatShortDate } from '../../lib/format.js';
import { formatRelativeTime } from '../../lib/notificationFeed.js';
import type { Signal } from '../../lib/signals.js';
import { SignalIcon } from './SignalIcon.js';
import { SignalRunButtonLink, SignalRunRowLink } from './SignalsLink.js';

const TAG_SX = {
  px: '8px',
  py: '3px',
  borderRadius: '6px',
  bgcolor: 'background.platform',
  color: 'text.secondary',
  fontSize: 11,
  fontWeight: 500,
  lineHeight: '16px',
  whiteSpace: 'nowrap',
} as const;

const BADGE_SX = {
  px: '6px',
  py: '4px',
  borderRadius: '6px',
  bgcolor: 'primary.bg',
  color: 'primary.main',
  fontSize: 10,
  fontWeight: 700,
  lineHeight: '12px',
  letterSpacing: '0.8px',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
} as const;

interface SignalRowProps {
  signal: Signal;
  unread: boolean;
  variant: 'compact' | 'full';
  dismissed?: boolean;
  onOpen: (signal: Signal) => void;
  onDismiss?: (signal: Signal) => void;
  onRestore?: (signal: Signal) => void;
}

const ROW_NOUNS: Record<string, string> = {
  combination_list: 'combination',
  element_list: 'element',
};

function itemsLabel(count: number, resultKind: string | null): ReactNode {
  const noun = (resultKind === null ? undefined : ROW_NOUNS[resultKind]) ?? 'creative';
  return (
    <>
      <Box component="b" sx={{ color: 'text.primary', fontWeight: 600 }}>
        {count}
      </Box>{' '}
      {noun}
      {count === 1 ? '' : 's'}
    </>
  );
}

const SEVERITY_SX = {
  high: { bgcolor: 'error.bg', color: 'error.main' },
  medium: { bgcolor: 'warning.bg', color: 'warning.main' },
} as const;

function SignalMeta({ signal }: { signal: Signal }): ReactNode {
  return (
    <Stack
      direction="row"
      sx={{ columnGap: '8px', rowGap: '4px', alignItems: 'center', flexWrap: 'wrap' }}
    >
      <Box component="span" sx={TAG_SX}>
        {signal.workflow}
      </Box>
      {signal.severityLabel === null ? null : (
        <Box
          component="span"
          sx={[BADGE_SX, SEVERITY_SX[signal.severity as keyof typeof SEVERITY_SX] ?? {}]}
          data-testid="signal-severity"
        >
          {signal.severityLabel}
        </Box>
      )}
      {signal.kpiLabel === null ? null : (
        <Box component="span" sx={BADGE_SX} data-testid="signal-kpi">
          {signal.kpiLabel}
        </Box>
      )}
      <Typography component="span" sx={{ fontSize: 11, color: 'text.secondary' }}>
        {formatRelativeTime(new Date(signal.completedAt))}
      </Typography>
    </Stack>
  );
}

function SignalTitle({ signal, unread }: { signal: Signal; unread: boolean }): ReactNode {
  return (
    <Stack direction="row" sx={{ gap: '8px', alignItems: 'center' }}>
      {unread ? (
        <Box
          component="img"
          src={unreadUrl}
          alt="Unread"
          width={7}
          height={7}
          sx={{ flexShrink: 0 }}
        />
      ) : null}
      <Typography
        sx={{ fontSize: 14, fontWeight: 600, lineHeight: '20px', color: 'background.icon' }}
      >
        {signal.summary ?? signal.workflow}
      </Typography>
    </Stack>
  );
}

function SignalDescription({ signal }: { signal: Signal }): ReactNode {
  const accounts = signal.accountNames.join(', ');
  const baseline =
    signal.startDate === '' || signal.endDate === ''
      ? null
      : `Baseline ${formatShortDate(signal.startDate)} – ${formatShortDate(signal.endDate)}`;
  return (
    <Typography
      sx={{ fontSize: 12, lineHeight: '18px', color: 'text.secondary' }}
      data-testid="signal-description"
    >
      {accounts === '' ? null : <>{accounts} · </>}
      {itemsLabel(signal.itemCount, signal.resultKind)}
      {baseline === null ? null : <> · {baseline}</>}
    </Typography>
  );
}

export function SignalRow({
  signal,
  unread,
  variant,
  dismissed = false,
  onOpen,
  onDismiss,
  onRestore,
}: SignalRowProps): ReactNode {
  if (variant === 'compact') {
    return (
      <Box
        component="li"
        data-testid="signal-row"
        sx={{ listStyle: 'none', borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <SignalRunRowLink
          signal={signal}
          onClick={() => onOpen(signal)}
          aria-label={`Open result: ${signal.summary ?? signal.workflow}`}
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            textAlign: 'left',
            gap: '12px',
            p: '16px 20px 16px 24px',
            color: 'inherit',
            '&:hover': { bgcolor: 'background.page' },
            '&.Mui-focusVisible, &:focus-visible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: '-2px',
            },
          }}
        >
          <SignalIcon icon={signal.icon} />
          <Stack sx={{ gap: '6px', flex: 1, minWidth: 0 }}>
            <SignalMeta signal={signal} />
            <SignalTitle signal={signal} unread={unread} />
          </Stack>
        </SignalRunRowLink>
      </Box>
    );
  }

  return (
    <Box
      component="li"
      data-testid="signal-row"
      sx={{
        listStyle: 'none',
        display: 'flex',
        gap: '16px',
        alignItems: 'center',
        p: '16px 16px 16px 20px',
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <SignalIcon icon={signal.icon} />
      <Stack sx={{ gap: '4px', flex: 1, minWidth: 0 }}>
        <SignalMeta signal={signal} />
        <SignalTitle signal={signal} unread={unread} />
        <SignalDescription signal={signal} />
      </Stack>
      <SignalRunButtonLink
        signal={signal}
        onClick={() => onOpen(signal)}
        aria-label={`Open result: ${signal.summary ?? signal.workflow}`}
        sx={{
          width: 200,
          flexShrink: 0,
          justifyContent: 'space-between',
          gap: '8px',
          p: '7px 11px',
          borderRadius: '9px',
          border: '1px solid',
          borderColor: 'text.muted',
          bgcolor: 'background.paper',
          color: 'text.primary',
          fontSize: 12,
          fontWeight: 700,
          lineHeight: '16px',
          textTransform: 'none',
          '&:hover': { bgcolor: 'background.page' },
        }}
      >
        <Box component="span" sx={{ flex: 1, textAlign: 'center' }}>
          Open result
        </Box>
        <Box component="img" src={caretRightUrl} alt="" width={16} height={16} />
      </SignalRunButtonLink>
      {dismissed ? (
        <Button
          size="small"
          onClick={() => onRestore?.(signal)}
          aria-label={`Restore signal: ${signal.summary ?? signal.workflow}`}
          sx={{ fontSize: 12, fontWeight: 600, color: 'primary.main', flexShrink: 0 }}
        >
          Restore
        </Button>
      ) : (
        <ButtonBase
          onClick={() => onDismiss?.(signal)}
          aria-label={`Dismiss signal: ${signal.summary ?? signal.workflow}`}
          sx={{
            width: 30,
            height: 30,
            flexShrink: 0,
            borderRadius: '8px',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': { bgcolor: 'background.page' },
            '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main' },
          }}
        >
          <Box component="img" src={closeUrl} alt="" width={16} height={16} />
        </ButtonBase>
      )}
    </Box>
  );
}
